import { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Swal from 'sweetalert2'

import MiniHeader from '../../../../UI/MiniHeader'
import Btn from '../../../../UI/Btn'
import CompanyProgress from '../../../../UI/CompanyProgress'
import VisibilityAccess from '../../../VisibilityAccess'

import BasicInfoTab from '../../common/CompanyProfile/components/BasicInfoTab'
import BusinessTab from '../../common/CompanyProfile/components/BusinessTab'
import RiskTab from '../../common/CompanyProfile/components/RiskTab'
import ContactTab from '../../common/CompanyProfile/components/ContactTab'
import PeopleTab, { VIEWS } from '../../common/CompanyProfile/components/PeopleTab'
import ExpertsTab from '../../common/CompanyProfile/components/ExpertsTab'
import ExpertsFlowTab from '../../common/CompanyProfile/components/ExpertsFlowTab'
import SchoolTeachersSection from '../../common/CompanyProfile/components/SchoolTeachersSection'
import DocumentsTab from '../../common/CompanyProfile/components/DocumentsTab'
import SharesTab from '../../common/CompanyProfile/components/SharesTab'
import LogTab from '../../common/CompanyProfile/components/LogTab'

import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { TApi, TReject, TRoutes, TRoles } from '../../../../shared/const'
import { useIsReadOnly } from '../../../../shared/alumni'
import { blobTypes, downloadFile, exportFileName } from '../../../../shared/helpers'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { isDialogOpen } from '../../../../shared/useDialogDismiss'
import { getActivationRequirements } from '../../../../shared/activationRequirements'

import styles from '../../common/CompanyProfile/style.module.scss'

const TABS = [
	{ key: 'basic', label: 'Basic Information' },
	{ key: 'business', label: 'Business Activity' },
	{ key: 'risk', label: 'Risk Analysis' },
	{ key: 'people', label: 'employees' },
	{ key: 'experts', label: 'Economic experts' },
	// Untermenuepunkt zu den Wirtschaftsexpert:innen: Die Kurzanleitung erklaert,
	// warum es dort nichts zuzuweisen gibt. Als eigener Punkt daneben („Weg zur
	// Expert:in") las sie sich wie ein zweiter Bestand.
	{ key: 'expertsFlow', label: 'Quick guide', parent: 'experts' },
	{ key: 'documents', label: 'documents' },
	// Wer Anteile gezeichnet hat. Steht neben den Dokumenten, weil die
	// Anteilscheine und die Einverstaendniserklaerungen dort liegen.
	{ key: 'shares', label: 'Company Shares' },
	{ key: 'log', label: 'Comments' }
]

/*
 * Alumni (#213) lesen das Profil ohne Dokumente und ohne die internen
 * Kommentare der Verwaltung — der Server liefert beides für sie nicht aus.
 */
const ALUMNI_HIDDEN_TABS = ['documents', 'log']

/*
 * Der Abschnitt steht als `?tab=` in der Adresszeile — wie im Profil der
 * Schueler:innen. Die Erinnerung „Offene Pruefungen" fuehrt damit gleich zu den
 * Mitarbeiter:innen (`?tab=people`), wo die Elternbestaetigungen geprueft
 * werden, und ein Neuladen bleibt im gewaehlten Abschnitt stehen.
 * Ein unbekannter Schluessel faellt auf den ersten Abschnitt zurueck.
 */
const tabFromSearch = search => {
	const wanted = new URLSearchParams(search).get('tab')
	return TABS.some(tab => tab.key === wanted) ? wanted : TABS[0].key
}

/**
 * In welchem Zustand ist diese Junior Company?
 *
 * Die Ampeln am Untermenue sagen, was an einem Abschnitt noch fehlt — sie
 * sagen nicht, ob das Unternehmen ueberhaupt schon aktiviert ist. Bis
 * 08.08.2026 stand das nirgends: Man las es aus den Knoepfen ab („Aktivieren"
 * ist da, also ist sie es nicht) oder gar nicht.
 *
 * Reihenfolge: Eine Ablehnung ueberschreibt alles Weitere, danach zaehlt die
 * Aktivierung, dann die eingereichte Pruefung.
 */
const companyStatus = companyInfo => {
	if (!companyInfo) return null
	if (companyInfo.isRejected) return { key: 'rejected', label: 'Company Rejected', tone: 'Red' }
	if (companyInfo.isActive) return { key: 'active', label: 'Company status active', tone: 'Green' }
	if (companyInfo.isNeedReview) return { key: 'review', label: 'Company status in review', tone: 'Amber' }
	return { key: 'draft', label: 'Company status not activated', tone: 'Grey' }
}

// Merkt sich, ob die Mitarbeiter:innen zuletzt als Liste oder als Kacheln
// angesehen wurden - die Wahl soll den naechsten Aufruf ueberleben.
const VIEW_STORAGE_KEY = 'companyReview.peopleView'

function CompanyReview(props) {
	const { t } = useTranslation()
	const history = useHistory()
	const location = useLocation()
	const { id } = useParams()

	const [isLoadingReject, setLoadingReject] = useState(false)
	const [isLoadingAct, setLoadingAct] = useState(false)
	const [isLoadingExport, setLoadingExport] = useState(false)
	const [isLoadingDelete, setLoadingDelete] = useState(false)
	const [company, setCompany] = useState({})
	const [activeTab, setActiveTab] = useState(() => tabFromSearch(location.search))
	const readOnly = useIsReadOnly()
	const tabs = readOnly ? TABS.filter(tab => !ALUMNI_HIDDEN_TABS.includes(tab.key)) : TABS
	const [peopleView, setPeopleView] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || VIEWS.GRID)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(activeTab, tabsRef)

	// Ein weiterer Verweis auf dasselbe Profil (anderer Abschnitt) wechselt mit.
	useEffect(() => {
		setActiveTab(current => {
			const wanted = tabFromSearch(location.search)
			return wanted === current ? current : wanted
		})
	}, [location.search])

	const openTab = key => {
		setActiveTab(key)
		history.replace({ pathname: location.pathname, search: `?tab=${key}` })
	}

	const {
		companyInfo,
		employees,
		invitationUser,
		experts,
		supervision,
		teachers,
		businessPlan,
		riskAnalysis,
		companyCertificate,
		finalReport,
		// Wo die Company im Programm steht (Anteile, Berichte, Rechnungen,
		// Geschäftsbericht) — dieselbe Auskunft wie auf den Dashboards,
		// gerechnet in services/companies/activation.
		activationsStatus,
		// Freigabe zur Überarbeitung für die Schüler:innen (#230), vom Server
		// gerechnet: ob sie gilt und bis wann.
		revisionRelease
	} = company

	/*
	 * Geschäftstätigkeit bearbeiten und freigeben (#230): Bundesadmins bei
	 * jeder Company, Landesadmins nur im eigenen Bundesland — dieselbe Grenze
	 * wie im Server. Alumni nie. Kein Knopf, den der Server ablehnen würde.
	 */
	const role = useSelector(state => state.user.role)
	const ownState = useSelector(state => state.user.profile?.state)
	const canEditBusiness =
		!readOnly &&
		!!companyInfo &&
		(role === TRoles.FEDERAL_ADMIN || (role === TRoles.ADMIN && !!ownState && ownState === companyInfo.state))

	const [newEmailValue, setNewEmailValue] = useState(companyInfo?.email)
	const [newNameValue, setNewNameValue] = useState(companyInfo?.name)

	const [isEmailChanging, setIsEmailChanging] = useState(false)
	const [isNameChanging, setIsNameChanging] = useState(false)

	const [isSendingEmailVerify, setIsSendingEmailVerify] = useState(false)

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein <img src>
	// könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarUrls = useAvatarUrls([
		...(employees || []).map(item => item.avatarHashName),
		...(invitationUser || []).map(item => item.avatarHashName),
		...(experts || []).map(item => item.avatarHashName),
		...(teachers || []).map(item => item.Teacher?.avatarHashName),
		supervision?.Teacher?.avatarHashName
	])

	const onVerifyBtnClick = async () => {
		// Die Verwaltung hat keine eigene Firma — welche gemeint ist, muss mit (#220).
		http.post(TApi.STUDENT_VERIFY_EMAIL_COMPANY, { email: companyInfo?.email, companyId: companyInfo?.id ?? id })
			.then(({ data }) => {
				setIsSendingEmailVerify(true)
				toast.success(t('Verify_company_email_sent_to', { email: data?.sentTo || companyInfo?.email }))
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setIsSendingEmailVerify(false))
	}

	const changeCompanyEmailByAdmins = async () => {
		if (companyInfo?.email !== newEmailValue) {
			// `disableToast`: Der eigene Satz nennt den wahrscheinlichen Grund.
			http.put(
				TApi.COMPANY_CHANGE_EMAIL_BY_ADMIN(id),
				{
					newEmail: newEmailValue,
					oldEmail: companyInfo?.email
				},
				{ disableToast: true }
			)
				.then(({ data }) => {
					setNewEmailValue(data.newEmail)
					toast.success(t('Mail successfully changed'))
					window.location.reload()
				})
				.catch(error => notifyError(error, 'No company with such an old email was found'))
		}

		setIsEmailChanging(false)
	}

	const changeCompanyNameByAdmins = async () => {
		if (companyInfo?.name !== newNameValue) {
			// `disableToast`: Der eigene Satz nennt den wahrscheinlichen Grund.
			http.put(
				TApi.COMPANY_CHANGE_NAME_BY_ADMIN(id),
				{
					newName: newNameValue,
					oldName: companyInfo?.name
				},
				{ disableToast: true }
			)
				.then(async ({ data }) => {
					setNewNameValue(data.newName)

					// Bei einer aktiven Company erzeugt das Backend den
					// Firmenbuchauszug beim Umbenennen sofort neu. Das passiert
					// unsichtbar - wer es nicht weiss, arbeitet weiter mit dem
					// alten Auszug.
					if (companyInfo?.isActive) {
						await Swal.fire({
							icon: 'info',
							title: t('Company certificate renewed'),
							html: t('Company certificate renewed details'),
							confirmButtonColor: '#008b9c',
							confirmButtonText: t('Understood')
						})
					} else {
						toast.success(t('Company Name successfully changed'))
					}

					window.location.reload()
				})
				.catch(error => {
					notifyError(error, 'No company with such an old name was found')
					setNewNameValue(companyInfo?.name)
				})
		}
		setIsNameChanging(false)
	}

	useEffect(() => {
		if (companyInfo?.email) {
			setNewEmailValue(companyInfo.email)
		}
	}, [companyInfo?.email])

	useEffect(() => {
		if (companyInfo?.name) {
			setNewNameValue(companyInfo.name)
		}
	}, [companyInfo?.name])

	const handleChangeView = view => {
		setPeopleView(view)
		localStorage.setItem(VIEW_STORAGE_KEY, view)
	}

	/**
	 * Vor dem Aktivieren nach einem Kommentar fragen. Er ist freiwillig — wer
	 * nichts zu sagen hat, aktiviert einfach. Der Text landet am Ereignis im
	 * Verlauf, damit dort nicht nur „Unternehmen aktiviert" ohne Begruendung
	 * steht; die Ablehnung macht es seit jeher genauso.
	 */
	const handleActivateCompany = async () => {
		const { isConfirmed, value } = await Swal.fire({
			title: t('Activate Company'),
			text: t('Comment on the activation (optional)'),
			input: 'textarea',
			inputPlaceholder: t('Type comment about company'),
			inputAttributes: { 'aria-label': t('Comment on the activation (optional)') },
			showCancelButton: true,
			confirmButtonColor: '#2e9b5b',
			cancelButtonColor: '#6c757d',
			confirmButtonText: t('Activate Company'),
			cancelButtonText: t('Cancel')
		})

		if (!isConfirmed) return

		setLoadingAct(true)
		http.put(TApi.ADMIN_PUT_COMPANY_ACTIVATION(companyInfo.id), { comment: (value || '').trim() }).then(() => {
			toast.success(t('Company approved'))
			setCompany({
				...company,
				companyInfo: {
					...company.companyInfo,
					isActive: true
				}
			})
			setLoadingAct(false)
		})
	}

	/**
	 * Export und Löschen kommen aus dem Drei-Punkte-Menü der Liste. Wer eine
	 * Company erst prüft und dann handeln will, soll dafür nicht in die Liste
	 * zurückgehen müssen — die Handlungen gehören dorthin, wo der Datensatz
	 * offen ist. Die Bedingungen sind dieselben wie in der Liste.
	 */
	const handleExportCompany = () => {
		setLoadingExport(true)
		http.get(TApi.ADMIN_DOWNLOAD_COMPANY_DATA(companyInfo.id), { responseType: 'arraybuffer' })
			.then(res => {
				downloadFile(exportFileName(companyInfo.name), new Blob([res.data], { type: blobTypes.XLS }))
			})
			.finally(() => setLoadingExport(false))
	}

	// Dieselbe Bedingung wie in der Liste: Eine aktive Company oder eine mit
	// Berichten wird nicht gelöscht.
	const canDeleteCompany =
		Boolean(companyInfo) && !companyInfo.isActive && !companyInfo.SemiannualReport && !companyInfo.AnnualReport

	/*
	 * Ablehnen geht nur bis zum ersten abgegebenen Bericht (08.08.2026).
	 * Danach hängen Rechnung, Anteile und der Bericht selbst an diesem
	 * Unternehmen — eine Ablehnung würde die Company aus der Prüfung nehmen
	 * und diese Daten ohne geklärten Umgang stehen lassen. Der Knopf bleibt
	 * sichtbar und sagt im Hinweis, warum er nicht mehr geht.
	 */
	const hasSubmittedReport = Boolean(
		companyInfo?.SemiannualReport?.isSubmitted || companyInfo?.AnnualReport?.isSubmitted
	)
	const canRejectCompany = Boolean(companyInfo) && !companyInfo.isRejected && !hasSubmittedReport

	const handleDeleteCompany = async () => {
		const { isConfirmed } = await Swal.fire({
			title: t('Are you sure?'),
			html: `<b>„${companyInfo?.name}"</b><br/><br/>${t('You will not be able to revert this!')}<br/><br/>${t(
				'Type {{word}} to confirm',
				{ word: t('Delete') }
			)}`,
			icon: 'warning',
			input: 'text',
			inputPlaceholder: t('Delete'),
			inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
			inputValidator: value =>
				(value || '').trim().toLowerCase() !== t('Delete').toLowerCase()
					? t('Input does not match')
					: undefined,
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#6c757d',
			confirmButtonText: t('Delete'),
			cancelButtonText: t('Cancel')
		})

		if (!isConfirmed) return

		setLoadingDelete(true)
		try {
			await http.delete(TApi.ADMIN_DELETE_COMPANY(companyInfo.id))
			toast.success(t('The company successfully deleted'))
			history.push(TRoutes.ADMIN_COMPANIES)
		} finally {
			setLoadingDelete(false)
		}
	}

	const handleRejectCompany = () => {
		setLoadingReject(true)

		eventEmitter.emit(types.openRejectCompanyCommentModal, id, TReject.COMPANY, () => {
			setCompany({
				...company,
				companyInfo: {
					...company.companyInfo,
					isNeedReview: false,
					isRejected: true
				}
			})
			history.push(TRoutes.ADMIN_COMPANIES)
		})
		setLoadingReject(false)
	}

	const reloadCompany = () => http.get(TApi.ADMIN_GET_COMPANY(id)).then(({ data }) => setCompany(data))

	const handleRejectParental = (userId, companyId, firstName, gender) => {
		setLoadingReject(true)
		eventEmitter.emit(types.openRejectCompanyCommentModal, userId, TReject.PARENTAL, reloadCompany, {
			firstName,
			gender
		})
		setLoadingReject(false)
	}

	const handleApproveParental = (userId, index) => {
		http.put(TApi.ADMIN_REVIEW_PARENTAL_CONFIRMATION(userId)).then(() => {
			const _users = employees.slice()
			_users[index] = {
				..._users[index],
				reviewedParentalConfirmation: true,
				rejectedParentalConfirmation: false
			}
			setCompany({ ...company, employees: _users })
			toast.success(t('Successfully updated'))
		})
	}

	const handleDocumentsChanged = (key, document) => setCompany(current => ({ ...current, [key]: document }))

	// An der Kennung, nicht nur beim ersten Aufbau: Wechselt die Adresse von
	// einem Firmenprofil zum naechsten, bleibt die Komponente stehen — mit `[]`
	// stuenden dann die Stammdaten der vorigen Company neben den Daten der neuen.
	useEffect(() => {
		reloadCompany()

		/* eslint-disable-next-line */
	}, [id])

	useEffect(() => {
		const handleEsc = e => {
			if (e.key !== 'Escape') return
			// Don't navigate away while the user is typing in a field
			const tag = document.activeElement?.tagName
			if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
			// Skip if a sweetalert/modal is open
			if (document.querySelector('.swal2-container')) return
			if (isDialogOpen()) return
			history.goBack()
		}
		document.addEventListener('keydown', handleEsc)
		return () => document.removeEventListener('keydown', handleEsc)
	}, [history])

	/*
	 * Was der Server misst, bevor er aktiviert.
	 *
	 * Die Ampeln an den Reitern darunter hängen an den Daten dieser Ansicht
	 * (`activations`) — sie sagen, wo etwas nachzutragen ist. Der Knopf hängt
	 * seit 21.08.2026 an derselben Liste wie die Checkliste der Schüler:innen
	 * und wie die Sperre im Backend: Sonst wäre er hier bedienbar, während der
	 * Server ablehnt. Die Warteliste und die Arbeitsbereiche standen in
	 * `activations` nämlich gar nicht.
	 */
	const openRequirements = getActivationRequirements({
		options: props.options,
		activationsStatus
	}).filter(item => item.status !== true)

	const activations = {
		basicInfo: true,
		employees: employees?.length >= 2 && employees.every(employee => !!employee.reviewedParentalConfirmation),
		// Wie der Server zaehlt: verbundene Unterstuetzer:innen plus Altbestand
		// (countAssignedExperts). `experts` ist nur der Altbestand — den gibt es
		// auf Prod nicht mehr, und der Knopf blieb bei jeder Company grau (#193).
		experts: activationsStatus ? !!activationsStatus.experts : experts?.length > 0,
		supervision: !!supervision,
		contacts: !!(companyInfo?.email && companyInfo?.phone),
		school: supervision?.Teacher?.School,
		// Die zweite, laengere Beschreibung ist am 08.08.2026 entfallen — es
		// bleiben Angebotsart und Beschreibung.
		businessActivity: !!(companyInfo?.description && companyInfo?.offerTypes),
		businessIdea: !!companyInfo?.businessIdea,
		documents: !!riskAnalysis
	}

	// Welche Bedingungen haengen an welchem Reiter? So sieht man am Reiter
	// selbst, wo noch etwas fehlt, ohne jeden einzeln zu oeffnen.
	const tabStatus = {
		// Kontaktdaten sowie Schule und Lehrkraft stecken im selben Reiter, also
		// haengt seine Ampel auch an ihnen.
		basic: activations.basicInfo && activations.contacts && activations.supervision && !!activations.school,
		business: activations.businessActivity && activations.businessIdea,
		risk: activations.documents,
		people: activations.employees,
		experts: activations.experts,
		// Reine Erklaerung, nichts abzuhaken.
		expertsFlow: null,
		documents: null,
		// Anteile sind keine Voraussetzung fuer die Aktivierung — sie kommen
		// danach.
		shares: null,
		log: null
	}

	const renderTab = () => {
		// Auch ein Reiter, der für Alumni ausgeblendet ist, darf über keinen
		// Umweg (alte Auswahl, Verweis) gezeichnet werden.
		switch (readOnly && ALUMNI_HIDDEN_TABS.includes(activeTab) ? 'basic' : activeTab) {
			case 'business':
				return (
					<BusinessTab
						companyInfo={companyInfo}
						typeOfOffers={props.typeOfOffers}
						branch={props.branch}
						canEdit={canEditBusiness}
						revisionRelease={revisionRelease}
						onChanged={reloadCompany}
					/>
				)
			case 'risk':
				return <RiskTab riskAnalysis={riskAnalysis} />
			case 'experts':
				return <ExpertsTab companyId={id} experts={experts} avatarUrls={avatarUrls} />
			case 'expertsFlow':
				return <ExpertsFlowTab />
			case 'people':
				return (
					<PeopleTab
						readOnly={readOnly}
						withoutDocuments={readOnly}
						companyInfo={companyInfo}
						employees={employees}
						invitationUser={invitationUser}
						avatarUrls={avatarUrls}
						workAreas={props.workAreas}
						view={peopleView}
						onChangeView={handleChangeView}
						onRejectParental={handleRejectParental}
						onApproveParental={handleApproveParental}
					/>
				)
			case 'shares':
				// Alumni: die Liste ja, die Einverständniserklärung nicht.
				return readOnly ? <SharesTab companyId={id} downloadUrl={null} /> : <SharesTab companyId={id} />
			case 'log':
				return <LogTab companyId={id} />
			case 'documents':
				return (
					<DocumentsTab
						companyId={id}
						companyInfo={companyInfo}
						businessPlan={businessPlan}
						companyCertificate={companyCertificate}
						finalReport={finalReport}
						onDocumentsChanged={handleDocumentsChanged}
					/>
				)
			default:
				return (
					<div className={styles.stack}>
						<BasicInfoTab
							readOnly={readOnly}
							companyInfo={companyInfo}
							isNameChanging={isNameChanging}
							setIsNameChanging={setIsNameChanging}
							newNameValue={newNameValue}
							setNewNameValue={setNewNameValue}
							onSaveName={changeCompanyNameByAdmins}
						/>
						<ContactTab
							readOnly={readOnly}
							companyInfo={companyInfo}
							newEmailValue={newEmailValue}
							setNewEmailValue={setNewEmailValue}
							isEmailChanging={isEmailChanging}
							setIsEmailChanging={setIsEmailChanging}
							onSaveEmail={changeCompanyEmailByAdmins}
							onVerifyBtnClick={onVerifyBtnClick}
							isSendingEmailVerify={isSendingEmailVerify}
						/>
						<SchoolTeachersSection
							readOnly={readOnly}
							companyId={id}
							supervision={supervision}
							teachers={teachers}
							avatarUrls={avatarUrls}
						/>
					</div>
				)
		}
	}

	const status = companyStatus(companyInfo)

	return (
		<div className={styles.root}>
			{/* Der Firmenname gehoert in die Ueberschrift: Wer mehrere Profile
			    nebeneinander offen hat, sieht sonst siebenmal denselben Titel.
			    Die Handlungen stehen ganz rechts oben in derselben Zeile — dort
			    sind sie unabhaengig davon erreichbar, wie lang der offene Reiter
			    gerade ist. */}
			<MiniHeader
				className={styles.header}
				title={companyInfo?.name ? `${t('Company profile')} ${companyInfo.name}` : t('Company profile')}
				bg="#F8FAFF"
			>
				{/* Alumni (#213): keine Handlungen — weder Export noch Löschen,
				    Ablehnen oder Aktivieren. */}
				{!readOnly && (
					<div className={styles.headerActions}>
						{/* Aus dem Drei-Punkte-Menü der Liste: Was dort zu einer
					    Company möglich ist, ist es auch hier. */}
						<Btn
							loading={isLoadingExport}
							onClick={handleExportCompany}
							className={clsx(styles.actionBtn, styles.actionBtnNeutral)}
							iconClassName={styles.btnIconReset}
							icon="download"
							iconOrder={0}
							iconSize={{ w: 19, h: 19 }}
							iconColor="#008b9c"
						>
							{t('Export')}
						</Btn>
						<VisibilityAccess access={[TRoles.ADMIN, TRoles.FEDERAL_ADMIN]}>
							<Btn
								disabled={!canDeleteCompany}
								tooltip={
									canDeleteCompany ? undefined : t('Only companies without reports can be deleted.')
								}
								loading={isLoadingDelete}
								onClick={handleDeleteCompany}
								className={clsx(styles.actionBtn, styles.actionBtnReject)}
							>
								{t('Delete')}
							</Btn>
						</VisibilityAccess>
						{(companyInfo?.isNeedReview || companyInfo?.isActive) && (
							<Btn
								disabled={!canRejectCompany}
								tooltip={
									hasSubmittedReport
										? t('Rejection is only possible until the first report.')
										: undefined
								}
								className={clsx(styles.actionBtn, styles.actionBtnReject)}
								loading={isLoadingReject}
								onClick={handleRejectCompany}
							>
								{!isLoadingReject && (
									<svg
										className={styles.rejectBtnIcon}
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth={2}
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<circle cx="12" cy="12" r="10" />
										<path d="M15 9 9 15M9 9 15 15" />
									</svg>
								)}
								{companyInfo?.isRejected ? t('Company Rejected') : t('Reject Company')}
							</Btn>
						)}
						{companyInfo?.isNeedReview && !companyInfo?.isActive && (
							<Btn
								tooltip={
									openRequirements.length
										? `${t('Still open')}: ${openRequirements.map(item => item.title).join(' · ')}`
										: undefined
								}
								disabled={
									Object.values(activations).some(item => item === false) ||
									openRequirements.length > 0 ||
									companyInfo?.isRejected ||
									!companyInfo?.emailVerified
								}
								loading={isLoadingAct}
								onClick={handleActivateCompany}
								className={clsx(styles.actionBtn, styles.actionBtnActivate)}
								iconClassName={styles.btnIconReset}
								icon="checkForBox"
								iconOrder={0}
								iconSize={{ w: 19, h: 19 }}
								iconColor="#2e9b5b"
							>
								{t('Activate Company')}
							</Btn>
						)}
					</div>
				)}
			</MiniHeader>

			{/*
			 * Wo steht diese Junior Company im Programm? Derselbe Balken, den
			 * Schüler:innen und Lehrkraft auf ihrem Dashboard sehen — die
			 * Verwaltung las das bisher nur stückweise aus den Ampeln am
			 * Untermenü, und die enden bei der Aktivierung.
			 */}
			{!!companyInfo && (
				<CompanyProgress
					compact
					className={styles.progress}
					companyType={companyInfo.companyType}
					activationsStatus={activationsStatus}
					isActivated={!!companyInfo.isActive}
					isNeedReview={!!companyInfo.isNeedReview}
				/>
			)}

			{/*
			 * Die acht Abschnitte standen bis 08.08.2026 als waagrechte Reiter über
			 * dem Inhalt und brachen je nach Fensterbreite um. Senkrecht daneben
			 * steht jeder von Anfang an da — dieselbe Bauart wie in den
			 * Einstellungen und im Handbuch.
			 */}
			<div className={styles.layout}>
				<nav className={styles.nav} ref={tabsRef} aria-label={t('Company profile')}>
					{/*
					 * Der Zustand des Unternehmens steht über den Abschnitten und
					 * bleibt beim Blättern stehen: Ob eine Company aktiviert ist,
					 * entscheidet, wie alles darunter zu lesen ist.
					 */}
					{status && (
						<div className={styles.statusCard}>
							<span className={styles.statusCardLabel}>{t('Status')}</span>
							<span className={clsx(styles.statusPill, styles[`statusPill${status.tone}`])}>
								{t(status.label)}
							</span>
						</div>
					)}

					{tabs.map(tab => {
						const done = tabStatus[tab.key]
						const isActive = activeTab === tab.key
						return (
							<button
								key={tab.key}
								type="button"
								aria-current={isActive ? 'true' : undefined}
								className={clsx(
									styles.navItem,
									tab.parent && styles.navItemChild,
									isActive && styles.navItemActive
								)}
								onClick={() => openTab(tab.key)}
							>
								{done !== null ? (
									<span
										className={clsx(styles.tabDot, done ? styles.tabDotDone : styles.tabDotOpen)}
										title={done ? t('Complete') : t('Incomplete')}
									/>
								) : (
									<span className={styles.tabDotPlaceholder} />
								)}
								{t(tab.label)}
							</button>
						)
					})}
				</nav>

				<div className={styles.content}>
					<div className={styles.panel}>{renderTab()}</div>
				</div>
			</div>
		</div>
	)
}

const mapStateToProps = state => ({
	options: state.options,
	workAreas: state.options.workAreas,
	typeOfOffers: state.options.typeOfOffers,
	branch: state.options.branch
})

export default connect(mapStateToProps)(CompanyReview)
